<template>
  <div class="limit-table">
    <div class="bg-white p-3 page-head">
      <div class="d-flex justify-content-between align-items-center">
        <h1>Transaction Limits</h1>
        <button
          @click="showAddForm = true"
          type="button"
          class="btn btn-primary"
        >
          <span class="ti-plus mr-2"></span>
          Add Transaction Limit
        </button>
      </div>
      <div class="d-flex">
        <select
          @change="handleAccountTypeChange"
          v-model="selectedAccountType"
          class="mr-3"
        >
          <option v-for="type in accountTypes" :key="type" :value="type">
            {{ formatText(type) }}
          </option>
        </select>
        <select
          @change="handleAccountCategoryChange"
          v-model="selectedAccountCategory"
          class="ml-3 mr-3"
        >
          <option
            v-for="category in accountCategories"
            :key="category"
            :value="category"
          >
            {{ formatText(category) }}
          </option>
        </select>
        <button class="btn btn-primary" @click="refresh">Refresh</button>
      </div>
    </div>

    <div class="spinner-grow" role="status" v-if="loading === true"></div>
    <div v-if="errorMsg" class="alert alert-danger">{{ errorMsg }}</div>

    <div class="">
      <card class="card-plain">
        <div class="table-full-width table-responsive table-container">
          <table class="table">
            <thead class="tms-dark">
              <tr>
                <th class="text-center">S/N</th>
                <th class="text-center">Account Type</th>
                <th class="text-center">Account Category</th>
                <th class="text-center">Tier Level</th>
                <th class="text-center">
                  Transfer Limit
                  <span
                    class="ti-info-alt"
                    ref="transferTooltip"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Transfer limit capped at specified amount per transaction"
                  ></span>
                </th>
                <th class="text-center">
                  Daily Cumulative Limit
                  <span
                    class="ti-info-alt"
                    ref="cumulativeTooltip"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Daily cumulative limit capped at specified amount per day"
                  ></span>
                </th>
                <th class="text-center">Date Created</th>
                <th class="text-center">Date Updated</th>
                <th class="text-center">Action</th>
              </tr>
            </thead>
            <tbody class="bg-light">
              <tr
                v-for="(limit, index) in filteredTransactionLimit"
                :key="limit._id"
              >
                <td class="text-center">{{ index + 1 }}</td>
                <td class="text-center">{{ formatText(limit.accountType) }}</td>
                <td class="text-center">
                  {{ formatText(limit.businessCategory || "N/A") }}
                </td>
                <td class="text-center">{{ formatTier(limit.tierLevel) }}</td>
                <td class="text-center">
                  {{ formatAmount(limit.transferLimit) }}
                </td>
                <td class="text-center">
                  {{ formatAmount(limit.dailyCumulativeLimit) }}
                </td>
                <td class="text-center">{{ formatDate(limit.createdAt) }}</td>
                <td class="text-center">{{ formatDate(limit.updatedAt) }}</td>
                <td
                  class="text-center view-details"
                  @click="viewDetails(limit)"
                >
                  View Details
                </td>
              </tr>
              <tr v-if="filteredTransactionLimit.length === 0 && !loading">
                <td class="text-center" colspan="10">No records found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </card>
    </div>

    <div class="pagination" style="justify-content: space-between; width: 100%">
      <div
        class="to-flex"
        style="display: flex; align-items: center; gap: 10px"
      >
        <button
          class="btn"
          :disabled="hasPrevPage === false"
          @click="refresh('previous')"
        >
          Previous
        </button>

        <span>Page {{ currentPage }} of {{ totalPages }}</span>

        <button class="btn" :disabled="!hasNextPage" @click="refresh('next')">
          Next
        </button>
      </div>

      <div class="p-2">
        <div class="btn">Total Records: {{ totalDocs }}</div>
      </div>
    </div>

    <!-- <div class="mt-2 paginate-btns">
      <div class="p-2" v-if="hasPrevPage === true">
        <button type="submit" class="btn btn-primary mb-2 mt-4" @click="refresh('previous')">
          Previous Page
        </button>
      </div>
      <div>Page {{ currentPage }} of {{ totalPages }}</div>
      <div class="p-2" v-if="hasNextPage === true">
        <button type="submit" class="btn btn-primary mb-2 mt-4" @click="refresh('next')">
          Next Page
        </button>
      </div>
    </div>
    <div class="mt-2 d-flex justify-content-end align-items-center px-4" v-if="filteredTransactionLimit.length !== 0">
      <div class="btn">Total Records: {{ totalDocs }}</div>
    </div> -->

    <Modal :visible="showAddForm" @close="handleCloseAddModal">
      <AddTransactionLimit
        @close="handleCloseAddModal"
        @refresh="handleRefresh"
      />
    </Modal>

    <ViewTransLimitDetails
      v-if="showViewTransLimit"
      :id="selectedTransLimitId"
      @close="handleCloseViewModal"
      @refresh="handleRefresh"
    />
  </div>
</template>

<script>
import axios from "../store/axios";
import moment from "moment";
import Modal from "./Modals/Modal.vue";
import AddTransactionLimit from "./Modals/AddTransactionLimit.vue";
import ViewTransLimitDetails from "./Modals/ViewTransLimitDetails.vue";
import { Tooltip } from "bootstrap";

export default {
  components: {
    Modal,
    AddTransactionLimit,
    ViewTransLimitDetails,
  },

  data() {
    return {
      showAddForm: false,
      showViewTransLimit: false,
      selectedTransLimitId: null,
      loading: false,
      error: false,
      errorMsg: "",
      transactionLimit: [],
      filteredTransactionLimit: [],
      accountTypes: [],
      accountCategories: [],
      selectedAccountType: "All Account Types",
      selectedAccountCategory: "All Account Categories",
      currentPage: 1,
      limit: 50,
      totalPages: 0,
      totalDocs: 0,
      hasPrevPage: false,
      hasNextPage: false,
    };
  },

  methods: {
    handleRefresh() {
      this.selectedAccountType = "All Account Types";
      this.selectedAccountCategory = "All Account Categories";
      this.fetchTransactionLimits();
    },

    handleCloseAddModal() {
      this.showAddForm = false;
      this.handleRefresh();
    },

    handleCloseViewModal() {
      this.showViewTransLimit = false;
      this.handleRefresh();
    },

    formatText(value) {
      if (!value) return "N/A";
      return value
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    formatTier(tierLevel) {
      if (!tierLevel) return null;
      return tierLevel
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(
          /(\w)(\w*)/,
          (match, first, rest) => first.toUpperCase() + rest,
        );
    },

    formatAmount(amount) {
      return `₦${parseFloat(amount).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },

    async fetchTransactionLimits(page = 1) {
      this.loading = true;
      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/get-transaction-limits`,
          {
            page: page,
            limit: this.limit,
          },
        );
        const data = res.data.data;
        this.transactionLimit = data.docs;
        this.filteredTransactionLimit = data.docs;
        this.extractFilterOptions();
        this.currentPage = data.page;
        this.totalPages = data.totalPages;
        this.totalDocs = data.totalDocs;
        this.hasPrevPage = data.hasPrevPage;
        this.hasNextPage = data.hasNextPage;
      } catch (error) {
        this.error = true;
        this.errorMsg =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message;
      } finally {
        this.loading = false;
      }
    },

    extractFilterOptions() {
      this.accountTypes = [
        "All Account Types",
        ...new Set(this.transactionLimit.map((item) => item.accountType)),
      ];
      this.accountCategories = [
        "All Account Categories",
        ...new Set(
          this.transactionLimit
            .map((item) => item.businessCategory)
            .filter((category) => category && category !== "N/A"),
        ),
      ];
    },

    handleAccountTypeChange(event) {
      this.selectedAccountType = event.target.value;
      this.applyFilters();
    },

    handleAccountCategoryChange(event) {
      this.selectedAccountCategory = event.target.value;
      this.applyFilters();
    },

    applyFilters() {
      this.filteredTransactionLimit = this.transactionLimit.filter((item) => {
        const matchesAccountType =
          this.selectedAccountType === "All Account Types" ||
          item.accountType === this.selectedAccountType;
        const matchesAccountCategory =
          this.selectedAccountCategory === "All Account Categories" ||
          item.businessCategory === this.selectedAccountCategory;
        return matchesAccountType && matchesAccountCategory;
      });
      this.totalDocs = this.filteredTransactionLimit.length;
    },

    viewDetails(limit) {
      this.selectedTransLimitId = limit._id;
      this.showViewTransLimit = true;
    },

    refresh(direction) {
      const newPage =
        direction === "next" ? this.currentPage + 1 : this.currentPage - 1;
      this.selectedAccountType = "All Account Types";
      this.selectedAccountCategory = "All Account Categories";
      this.fetchTransactionLimits(newPage);
      // this.applyFilters();
    },
  },

  mounted() {
    this.fetchTransactionLimits(this.currentPage);

    const tooltips = [this.$refs.transferTooltip, this.$refs.cumulativeTooltip];
    tooltips.forEach((tooltip) => {
      if (tooltip) new Tooltip(tooltip);
    });
  },
};
</script>

<style scoped>
.page-head,
.alert {
  width: 98%;
}

h1 {
  color: black;
  font-size: 30px;
  font-weight: 700;
}

.btn {
  border-radius: 50px;
  height: 45px;
  background-color: #65b3c7 !important;
  border: none !important;
  display: flex;
  gap: "10px";
  align-items: center;
}

select {
  padding: 10px;
  border: 1px solid #f2f1ed;
  border-radius: 4px;
}

td {
  font-size: 15px;
}

.transaction {
  padding: 20px;
}

.tms-dark {
  background-color: #333f48;
  color: #fff;
}

.spinner-grow {
  position: fixed;
}

.table-container {
  overflow-x: auto;
}

.view-details {
  color: #65b3c7;
  cursor: pointer;
}

.ti-info-alt {
  font-size: 14px;
  font-weight: normal;
}
</style>
